"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronUp, CreditCard, ExternalLink, LogOut } from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { SidebarLoungePoints } from "@/components/lounge/sidebar-points";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { Wordmark } from "@/components/ui/wordmark";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { withOrgParam } from "@/lib/utils";
import { CallHistoryList } from "./call-history-list";
import { OrganizationSwitcher } from "./organization-switcher";
import { SidebarChatSearch, SidebarNewAction } from "./sidebar-actions";
import { StudioNav } from "./studio-nav";
import type { CallHistoryItem } from "./call-history-list";
import type { Organization } from "@/lib/types";
interface RealtimeSidebarProps {
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  historyItems: CallHistoryItem[];
  isHistoryLoading?: boolean;
  currentItemId?: string | null;
  onNewCall: () => void;
  onItemClick: (itemId: string) => void;
  onItemDeleted?: (itemId: string) => void;
  className?: string;
}
export function RealtimeSidebar(t0) {
  const $ = _c(101);
  const {
    organizations,
    selectedOrganization,
    onSelectOrganization,
    historyItems,
    isHistoryLoading: t1,
    currentItemId,
    onNewCall,
    onItemClick,
    onItemDeleted,
    className
  } = t0;
  const isHistoryLoading = t1 === undefined ? false : t1;
  let switcherOrganizations;
  let t2;
  if ($[0] !== organizations || $[1] !== selectedOrganization?.id) {
    switcherOrganizations = organizations.filter(_temp);
    t2 = switcherOrganizations.find(org_0 => org_0.id === selectedOrganization?.id) ?? null;
    $[0] = organizations;
    $[1] = selectedOrganization?.id;
    $[2] = switcherOrganizations;
    $[3] = t2;
  } else {
    switcherOrganizations = $[2];
    t2 = $[3];
  }
  const switcherSelectedOrganization = t2;
  const router = useRouter();
  const searchParams = useSearchParams();
  const orgIdParam = searchParams.get("orgId");
  const withOrg = path => withOrgParam(path, orgIdParam);
  const posthog = usePostHog();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  let t3;
  if ($[4] !== posthog || $[5] !== router || $[6] !== signOut) {
    t3 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://chat.llmgateway.io/login");
          }
        }
      });
    };
    $[4] = posthog;
    $[5] = router;
    $[6] = signOut;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const logout = t3;
  if (isUserLoading) {
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4"><Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Wordmark /><Badge>Voice</Badge></Link></div><StudioNav /></SidebarHeader>;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] !== className) {
      t5 = <Sidebar className={className}>{t4}</Sidebar>;
      $[9] = className;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  if (!user) {
    let t4;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Wordmark /><Badge>Voice</Badge></Link>;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    let t5;
    let t6;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="font-medium mb-2">Sign in required</div>;
      t6 = <p className="text-muted-foreground mb-3">Please sign in to start voice calls.</p>;
      $[12] = t5;
      $[13] = t6;
    } else {
      t5 = $[12];
      t6 = $[13];
    }
    let t7;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button>;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t4}<div className="w-full rounded-md border p-4 text-sm">{t5}{t6}<div className="flex items-center justify-end gap-2">{t7}<Button size="sm" variant="outline" asChild={true}><Link href="/signup">Create account</Link></Button></div></div></div><StudioNav /></SidebarHeader>;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    let t9;
    if ($[16] !== className) {
      t9 = <Sidebar className={className}>{t8}</Sidebar>;
      $[16] = className;
      $[17] = t9;
    } else {
      t9 = $[17];
    }
    return t9;
  }
  const T0 = Sidebar;
  const t4 = "icon";
  const t5 = (className ?? "") + " max-md:hidden";
  const T1 = SidebarHeader;
  const T2 = SidebarMenu;
  const T3 = SidebarMenuItem;
  const T4 = SidebarMenuButton;
  const t6 = "lg";
  const t7 = true;
  const t8 = "Lounge";
  const T5 = Link;
  const t9 = withOrg("/");
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Wordmark size="sm" iconBox={true} />;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== T5 || $[20] !== t9) {
    t11 = <T5 href={t9} prefetch={true}>{t10}</T5>;
    $[19] = T5;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== T4 || $[23] !== t11) {
    t12 = <T4 size={t6} asChild={t7} tooltip={t8}>{t11}</T4>;
    $[22] = T4;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== T3 || $[26] !== t12) {
    t13 = <T3>{t12}</T3>;
    $[25] = T3;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SidebarChatSearch disabled={true} />;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== onNewCall) {
    t15 = <SidebarNewAction label="New Call" onAction={onNewCall} />;
    $[29] = onNewCall;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== T2 || $[32] !== t13 || $[33] !== t15) {
    t16 = <T2>{t13}{t14}{t15}</T2>;
    $[31] = T2;
    $[32] = t13;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <StudioNav />;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== T1 || $[37] !== t16) {
    t18 = <T1>{t16}{t17}</T1>;
    $[36] = T1;
    $[37] = t16;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== onSelectOrganization || $[41] !== switcherOrganizations || $[42] !== switcherSelectedOrganization) {
    t20 = switcherOrganizations.length > 0 ? <SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={switcherOrganizations} selectedOrganization={switcherSelectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[40] = onSelectOrganization;
    $[41] = switcherOrganizations;
    $[42] = switcherSelectedOrganization;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== t20) {
    t21 = <div>{t19}{t20}</div>;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const t22 = sidebarState === "collapsed" && !isMobile;
  let t23;
  if ($[46] !== currentItemId || $[47] !== historyItems || $[48] !== isHistoryLoading || $[49] !== onItemClick || $[50] !== onItemDeleted) {
    t23 = <CallHistoryList items={historyItems} isLoading={isHistoryLoading} currentItemId={currentItemId} onItemClick={onItemClick} onItemDeleted={onItemDeleted} />;
    $[46] = currentItemId;
    $[47] = historyItems;
    $[48] = isHistoryLoading;
    $[49] = onItemClick;
    $[50] = onItemDeleted;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t22 || $[53] !== t23) {
    t24 = <div aria-hidden={t22} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t23}</div>;
    $[52] = t22;
    $[53] = t23;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  let t25;
  if ($[55] !== t21 || $[56] !== t24) {
    t25 = <SidebarContent className="overflow-hidden pb-2">{t21}{t24}</SidebarContent>;
    $[55] = t21;
    $[56] = t24;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <SidebarLoungePoints />;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  const t27 = switcherSelectedOrganization ?? organization;
  const t28 = !switcherSelectedOrganization;
  let t29;
  if ($[59] !== isOrgLoading || $[60] !== t27 || $[61] !== t28) {
    t29 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={t27} isLoading={isOrgLoading} isChatPlanOrg={t28} /></div>;
    $[59] = isOrgLoading;
    $[60] = t27;
    $[61] = t28;
    $[62] = t29;
  } else {
    t29 = $[62];
  }
  const t30 = user?.name ?? "User";
  let t31;
  if ($[63] !== user?.name) {
    t31 = user?.name?.split(" ").map(_temp2).join("").toUpperCase().slice(0, 2) ?? "U";
    $[63] = user?.name;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== t31) {
    t32 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t31}</span></div>;
    $[65] = t31;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  const t33 = user?.name;
  let t34;
  if ($[67] !== t33) {
    t34 = <span className="truncate font-semibold">{t33}</span>;
    $[67] = t33;
    $[68] = t34;
  } else {
    t34 = $[68];
  }
  const t35 = user?.email;
  let t36;
  if ($[69] !== t35) {
    t36 = <span className="truncate text-xs text-muted-foreground">{t35}</span>;
    $[69] = t35;
    $[70] = t36;
  } else {
    t36 = $[70];
  }
  let t37;
  if ($[71] !== t34 || $[72] !== t36) {
    t37 = <div className="grid flex-1 text-left text-sm leading-tight">{t34}{t36}</div>;
    $[71] = t34;
    $[72] = t36;
    $[73] = t37;
  } else {
    t37 = $[73];
  }
  let t38;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[74] = t38;
  } else {
    t38 = $[74];
  }
  let t39;
  if ($[75] !== t30 || $[76] !== t32 || $[77] !== t37) {
    t39 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t30} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t32}{t37}{t38}</SidebarMenuButton></DropdownMenuTrigger>;
    $[75] = t30;
    $[76] = t32;
    $[77] = t37;
    $[78] = t39;
  } else {
    t39 = $[78];
  }
  let t40;
  let t41;
  if ($[79] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <DropdownMenuItem asChild={true}><Link href="/pricing" prefetch={true}><CreditCard className="mr-2 h-4 w-4" />{"Membership & Billing"}</Link></DropdownMenuItem>;
    t41 = <DropdownMenuSeparator />;
    $[79] = t40;
    $[80] = t41;
  } else {
    t40 = $[79];
    t41 = $[80];
  }
  let t42;
  let t43;
  if ($[81] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llmgateway.io/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t43 = <DropdownMenuSeparator />;
    $[81] = t42;
    $[82] = t43;
  } else {
    t42 = $[81];
    t43 = $[82];
  }
  let t44;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <span>Theme</span>;
    $[83] = t44;
  } else {
    t44 = $[83];
  }
  let t45;
  let t46;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <DropdownMenuItem className="justify-between gap-3" onSelect={_temp3}>{t44}<div onClick={_temp4} onKeyDown={_temp5}><ThemeToggle className="shrink-0" size="compact" /></div></DropdownMenuItem>;
    t46 = <DropdownMenuSeparator />;
    $[84] = t45;
    $[85] = t46;
  } else {
    t45 = $[84];
    t46 = $[85];
  }
  let t47;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <LogOut className="mr-2 h-4 w-4" />;
    $[86] = t47;
  } else {
    t47 = $[86];
  }
  let t48;
  if ($[87] !== logout) {
    t48 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t40}{t41}{t42}{t43}{t45}{t46}<DropdownMenuItem onClick={logout}>{t47}Log out</DropdownMenuItem></DropdownMenuContent>;
    $[87] = logout;
    $[88] = t48;
  } else {
    t48 = $[88];
  }
  let t49;
  if ($[89] !== t39 || $[90] !== t48) {
    t49 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t39}{t48}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[89] = t39;
    $[90] = t48;
    $[91] = t49;
  } else {
    t49 = $[91];
  }
  let t50;
  if ($[92] !== t29 || $[93] !== t49) {
    t50 = <SidebarFooter>{t26}{t29}{t49}</SidebarFooter>;
    $[92] = t29;
    $[93] = t49;
    $[94] = t50;
  } else {
    t50 = $[94];
  }
  let t51;
  if ($[95] !== T0 || $[96] !== t18 || $[97] !== t25 || $[98] !== t5 || $[99] !== t50) {
    t51 = <T0 collapsible={t4} className={t5}>{t18}{t25}{t50}</T0>;
    $[95] = T0;
    $[96] = t18;
    $[97] = t25;
    $[98] = t5;
    $[99] = t50;
    $[100] = t51;
  } else {
    t51 = $[100];
  }
  return t51;
}
function _temp5(event_1) {
  return event_1.stopPropagation();
}
function _temp4(event_0) {
  return event_0.stopPropagation();
}
function _temp3(event) {
  return event.preventDefault();
}
function _temp2(n) {
  return n[0];
}
function _temp(org) {
  return org.kind === "default";
}